"use client";

import useTranslate from "@app/_hooks/useTranslate";
import { useAppForm } from "@features/forms/config";
import { useForm, ValidationError } from "@formspree/react";
import { Button } from "@ui/button";
import { DialogClose } from "@ui/dialog";
import { Field, FieldLabel, FieldLegend, FieldSet } from "@ui/field";
import { Heading } from "@ui/heading";
import { Paragraph } from "@ui/paragraph";
import { Loader2 } from "lucide-react";
import { ComponentProps } from "react";
import ProductCard from "src/components/product-card/ProductCard";
import z from "zod";

const moreVariantsFormSchema = z.object({
  name: z.string().min(1),
  email: z.email(),
  brand: z.string().min(1),
  product: z.string().min(1),
  message: z.string().min(1),
  currency: z.string().min(1),
  productKey: z.string().min(1),
});

export default function ProductMoreVariantsForm({
  currency,
  productKey,
  productTitle,
  productBrand,
  productImage,
  ...props
}: {
  currency: string;
  productKey: string;
  productTitle: string;
  productBrand: string;
  productImage: string;
} & ComponentProps<"form">) {
  const [state, handleSubmit] = useForm("xeaapbkq");
  const t = useTranslate();

  const form = useAppForm({
    defaultValues: {
      name: "",
      email: "",
      brand: productBrand,
      product: productTitle,
      message: "",
      currency,
      productKey,
    },
    validators: {
      onSubmitAsync: async ({ value }) => {
        const validation = moreVariantsFormSchema.safeParse(value);
        if (validation.success) {
          await handleSubmit(validation.data);
          return;
        }
        return {
          fields: Object.fromEntries(
            validation.error.issues.map((issue) => [
              issue.path.join("."),
              issue.message,
            ]),
          ),
        };
      },
    },
  });

  if (state.succeeded) {
    return (
      <div className="flex flex-col items-center justify-center gap-y-4">
        <Heading className="text-lg">{t("formSuccessHeading")}</Heading>
        <Paragraph>{t("formSuccessText")}</Paragraph>
        <DialogClose asChild>
          <Button variant="secondary">{t("close")}</Button>
        </DialogClose>
      </div>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        form.handleSubmit();
      }}
      {...props}
    >
      <FieldSet>
        <FieldLegend className="mb-2 text-2xl">{t("contactUs")}</FieldLegend>
        <ProductCard className="grid grid-cols-[96px_1fr]">
          <ProductCard.ImageFlat
            src={productImage}
            alt={productTitle}
            width={96}
            height={96}
          />
          <ProductCard.Content>
            <ProductCard.Brand>{productBrand}</ProductCard.Brand>
            <ProductCard.Title>{productTitle}</ProductCard.Title>
            <ProductCard.Brand>
              {t("partNo")}: {productKey}
            </ProductCard.Brand>
          </ProductCard.Content>
        </ProductCard>
        <form.AppField
          name="product"
          children={(field) => (
            <field.Input type="hidden" required disabled value={productTitle} />
          )}
        />
        <form.AppField
          name="brand"
          children={(field) => (
            <field.Input type="hidden" required disabled value={productBrand} />
          )}
        />
        <form.AppField
          name="productKey"
          children={(field) => (
            <field.Input type="hidden" required disabled value={productKey} />
          )}
        />
        <form.AppField
          name="currency"
          children={(field) => (
            <field.Input type="hidden" required disabled value={currency} />
          )}
        />
        <FieldLegend>{t("moreVariantsFormDescription")}</FieldLegend>
        <form.AppField
          name="name"
          children={(field) => (
            <Field
              invalid={!field.getMeta().isValid}
              dirty={field.getMeta().isDirty}
              touched={field.getMeta().isTouched}
              className="col-span-full"
            >
              <FieldLabel placeholder>{t("name")}</FieldLabel>
              <field.Input
                required
                autoComplete="name"
                placeholder={t("name")}
                type="text"
                value={field.state.value ?? ""}
                onChange={(event) => field.handleChange(event.target.value)}
              />
              <ValidationError
                className="mt-2 text-base"
                field="name"
                errors={state.errors}
              />
            </Field>
          )}
        />
        <form.AppField
          name="email"
          children={(field) => (
            <Field
              invalid={!field.getMeta().isValid}
              dirty={field.getMeta().isDirty}
              touched={field.getMeta().isTouched}
              className="col-span-full"
            >
              <FieldLabel placeholder>{t("email")}</FieldLabel>
              <field.Input
                required
                autoComplete="email"
                placeholder={t("email")}
                type="email"
                value={field.state.value}
                onChange={(event) => field.handleChange(event.target.value)}
              />
              <ValidationError
                className="mt-2 text-base"
                field="email"
                errors={state.errors}
              />
            </Field>
          )}
        />
        <form.AppField
          name="message"
          children={(field) => (
            <Field
              invalid={!field.getMeta().isValid}
              dirty={field.getMeta().isDirty}
              touched={field.getMeta().isTouched}
              className="col-span-full"
            >
              <field.Textarea
                required
                placeholder={t("moreVariantsFormMessage")}
                value={field.state.value ?? ""}
                onChange={(event) => field.handleChange(event.target.value)}
              />
              <ValidationError
                className="mt-2 text-base"
                field="message"
                errors={state.errors}
              />
            </Field>
          )}
        />
        <form.Subscribe
          selector={(state) => [
            state.isValid,
            state.isDirty,
            state.isSubmitting,
          ]}
          children={([isValid, isDirty, isSubmitting]) => (
            <Button
              className="col-span-full"
              type="submit"
              disabled={!isDirty || !isValid}
            >
              {isSubmitting ? (
                <Loader2 className="size-5 animate-spin" strokeWidth={2} />
              ) : (
                t("sendRequest")
              )}
            </Button>
          )}
        />
      </FieldSet>
    </form>
  );
}
